"use client";

import { ChangeEventHandler, FC, useRef, useState } from "react";
import { PortableTextBlock } from "next-sanity";
import { StaticAccordion, StaticGroupedFAQ, TypeAccordion } from "@/common/lib/types";
import { hasArrayElements } from "@/apiUtils/commonUtils";
import SearchIcon from "@/assets/icons/search.svg";
import StaticAccordianView from "./StaticAccordianView";
import clsx from "clsx";
import Image from "next/image";

interface StaticGroupedAccordionContentViewProps {
  content: StaticGroupedFAQ;
}
const StaticGroupedAccordionContentView: FC<StaticGroupedAccordionContentViewProps> = ({
  content,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const [selectedAccordionGroup, setSelectedAccordionGroup] = useState<StaticAccordion[]>(
    content?.items,
  );

  const searchFaqDescriptions = (item: TypeAccordion, keyword: string) => {
    const newContents: PortableTextBlock[] = [];
    if (hasArrayElements(item.content)) {
      item?.content.forEach((contentItem) => {
        if (hasArrayElements(contentItem?.children)) {
          contentItem.children.forEach((child) => {
            if (child.text && child.text.toLowerCase().search(keyword.toLowerCase()) > -1) {
              newContents.push(contentItem);
            }
          });
        }
      });
    }
    return newContents;
  };

  const searchSubTitle = (group: StaticAccordion, keyword: string) => {
    const newItems: TypeAccordion[] = [];
    if (hasArrayElements(group.items)) {
      group.items.some((item) => {
        if (item?.title && item.title.toLowerCase().search(keyword.toLowerCase()) > -1) {
          newItems.push(item);
        } else {
          const newContent = searchFaqDescriptions(item, keyword);
          if (newContent && hasArrayElements(newContent)) {
            newItems.push({ ...item, content: newContent });
          }
        }
      });
    }
    return newItems;
  };

  const searchFaqs = (keyword: string) => {
    setSearchTerm(keyword);
    if (keyword) {
      const filteredItems: StaticAccordion[] = [];
      content?.items?.forEach((group) => {
        if (group?.title && group.title.toLowerCase().search(keyword.toLowerCase()) > -1) {
          filteredItems.push(group);
        } else {
          const newItems = searchSubTitle(group, keyword);
          if (newItems && hasArrayElements(newItems)) {
            filteredItems.push({ ...group, items: newItems });
          }
        }
      });
      setSelectedAccordionGroup(filteredItems);
    } else {
      setSelectedAccordionGroup(content?.items);
    }
  };

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    const keyword = event.target.value;
    searchFaqs(keyword);
  };

  return (
    <div className="px-4 md:px-12">
      <h5 className="text-size-22 font-bold">{content?.title}</h5>
      <p className="my-4">{content?.subTitle}</p>
      {content?.enableSearch ? (
        <>
          <div className="flex flex-row-reverse border-b border-b-[#c4c4c4]">
            <Image src={SearchIcon} className="cursor-pointer" height={20} width={20} alt="search icon" onClick={() => searchRef.current?.focus()} />

            <input
              ref={searchRef}
              type="text"
              placeholder={content?.searchBoxPlaceholder}
              className="w-full border-0 p-4 text-sm font-normal shadow-none outline-none ring-0 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          {content?.suggestions && hasArrayElements(content?.suggestions) ? (
            <div className="flex flex-row flex-wrap gap-2 pb-2.5 pt-8 md:pb-5">
              <div className="mb-3 md:mb-1">
                <button
                  className={clsx(
                    "border p-4 text-sm",
                    searchTerm === ""
                      ? "border-[#EC5D3C] text-[#EC5D3C]"
                      : "border-black text-black",
                  )}
                  onClick={() => searchFaqs("")}
                >
                  ALL
                </button>
              </div>
              {content?.suggestions.map((each) => (
                <div className="mb-3 md:mb-1" key={each.key}>
                  <button
                    className={clsx(
                      "border p-4 text-sm",
                      searchTerm === each.title
                        ? "border-[#EC5D3C] text-[#EC5D3C]"
                        : "border-black text-black",
                    )}
                    onClick={() => searchFaqs(each.title)}
                  >
                    {each.title}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            ""
          )}
        </>
      ) : (
        ""
      )}
      {hasArrayElements(selectedAccordionGroup) ? (
        selectedAccordionGroup?.map((item: StaticAccordion) => (
          <StaticAccordianView
            content={item}
            key={item.key}
            className="!my-11 !px-0 md:!my-16"
            classNames={{ accordion: { title: "!text-sm" } }}
          />
        ))
      ) : (
        <div className="mt-16 flex items-center justify-center">
          <div className="w-full">
            <h5 className="mb-8 text-base font-semibold">{content?.emptySearchMessage}</h5>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaticGroupedAccordionContentView;
